<script setup>
import { computed, ref } from 'vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarGroupSeparator from './SidebarGroupSeparator.vue';

import { useSidebarContext } from './provider';
import { useEventListener } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';

const props = defineProps({
  isExpanded: { type: Boolean, default: false },
  group: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [Object, String], required: true },
  children: { type: Array, default: undefined },
  activeChild: { type: Object, default: undefined },
  hideHeader: { type: Boolean, default: false },
});

const { isAllowed } = useSidebarContext();
const { uiSettings } = useUISettings();
const scrollableContainer = ref(null);

const accessibleItems = computed(() =>
  props.children.filter(child => {
    return child.to && isAllowed(child.to);
  })
);

const hasAccessibleItems = computed(() => {
  return accessibleItems.value.length > 0;
});

const isScrollable = computed(() => {
  return props.group !== 'Folders' && accessibleItems.value.length > 24;
});

const scrollEnd = ref(false);

// set scrollEnd to true when the scroll reaches the end
useEventListener(scrollableContainer, 'scroll', () => {
  const { scrollHeight, scrollTop, clientHeight } = scrollableContainer.value;
  scrollEnd.value = scrollHeight - scrollTop === clientHeight;
});

const isFolderCollapsed = child => {
  if (!child.children || child.children.length === 0) return false;
  if (!child.folderId) return false;
  return uiSettings.value.collapsed_folders?.[child.folderId] || false;
};
</script>

<template>
  <SidebarGroupSeparator
    v-if="hasAccessibleItems && !hideHeader"
    v-show="isExpanded"
    :label
    :icon
    class="my-1"
  />
  <ul v-if="children.length" class="m-0 list-none reset-base relative group">
    <!-- Each element has h-8, which is 32px, we will show 7 items with one hidden at the end,
    which is 14rem. Then we add 16px so that we have some text visible from the next item  -->
    <div
      ref="scrollableContainer"
      :class="{
        'max-h-[calc(20rem+16px)] overflow-y-scroll no-scrollbar': isScrollable,
      }"
    >
      <template v-for="child in children" :key="child.name">
        <SidebarGroupLeaf
          v-show="isExpanded || activeChild?.name === child.name"
          v-bind="child"
          :active="activeChild?.name === child.name"
          :children="child.children"
          :folder-id="child.folderId"
          :color="child.color"
        />
        <li
          v-if="child.children && !isFolderCollapsed(child)"
          class="py-0.5 ltr:pl-2 rtl:pr-2 rtl:mr-2 ltr:ml-2 relative text-n-slate-11 child-item before:bg-n-slate-4 after:bg-transparent after:border-n-slate-4 before:left-0 rtl:before:right-0"
        >
          <ul class="m-0 list-none sidebar-group-children">
            <SidebarGroupLeaf
              v-for="grandchild in child.children"
              v-show="isExpanded || activeChild?.name === grandchild.name"
              v-bind="grandchild"
              :key="grandchild.name"
              :active="activeChild?.name === grandchild.name"
              nested
            />
          </ul>
        </li>
      </template>
    </div>
    <div
      v-if="isScrollable && isExpanded"
      v-show="!scrollEnd"
      class="absolute bg-gradient-to-t from-n-solid-2 w-full h-12 to-transparent -bottom-1 pointer-events-none flex items-end justify-end px-2 animate-fade-in-up"
    >
      <svg
        width="16"
        height="24"
        viewBox="0 0 16 24"
        fill="none"
        class="text-n-slate-9 opacity-50 group-hover:opacity-100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4 4L8 8L12 4"
          stroke="currentColor"
          opacity="0.5"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 10L8 14L12 10"
          stroke="currentColor"
          opacity="0.75"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          d="M4 16L8 20L12 16"
          stroke="currentColor"
          stroke-width="1.33333"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
  </ul>
</template>
